export interface MobileProduct {
  id: string;
  name: string;
  subtitle: string;
  description: string;
  images: string[];
  features: string[];
  specs?: Record<string, string>;
  highlights: { title: string; value: string; icon: string }[];
  pdfUrl?: string;
  // Structured sections for Tabs
  technicalData: Record<string, string>;
  mainCharacteristics: Record<string, string>;
  connectivity: Record<string, string>;
  physicalCharacteristics: Record<string, string>;
}

export const mobileProducts: MobileProduct[] = [
  {
    id: "rxis203",
    name: "RxIS203",
    subtitle: "Dual SIM IS-Mobile",
    description: `The RxIS203 is a robust intrinsically safe mobile device from Realix, designed for hazardous environments. It features a high-definition FHD display and a powerful 12MP camera for clear documentation in the field.
  
Designed for versatility, it supports Dual SIM Dual Standby functionality, allowing users to manage connectivity efficiently, or switch to a One SIM + One TF Card configuration for expanded storage. The device includes a specialized magnetic charging pin for safe and easy power connection and a loudspeaker designed for clear audio. It is built to withstand exposure to water and hazardous conditions.`,
    images: ["/images/products/mobile/rxis203-front.png"],
    features: [
      "INTRINSICALLY SAFE: Ex Certified for Hazardous Areas",
      "DISPLAY: FHD High Definition Screen",
      "CAMERA: 12MP Camera",
      "CHARGING: Magnetic Charging Pin",
      "SIM: Dual SIM Dual Standby / TF Card Support",
      "AUDIO: Amplified Loudspeaker",
    ],
    highlights: [
      { title: "Camera", value: "12MP", icon: "ion:camera" },
      { title: "Display", value: "FHD", icon: "ion:phone-portrait" },
    ],
    pdfUrl: "/documents/mobile/technical-specification/rxis203.pdf",
    technicalData: {
      Brand: "Realix",
      Model: "RxIS203",
      Camera: "12MP",
      "Charging Interface": "Magnetic charging pin",
    },
    mainCharacteristics: {
      "Display Resolution": "FHD (Full High Definition)",
      "Card Slot": "Dual SIM Card or One SIM + One TF Card",
      Durability: "Ex (Explosion Proof) Rated",
      Audio: "Loudspeaker",
    },
    connectivity: {
      "SIM Configuration": "Dual SIM Dual Standby",
      Expansion: "TF Card Slot",
      Charging: "Magnetic Pin",
    },
    physicalCharacteristics: {
      "Water Resistance": "Yes (See manual for exposure limits)",
      Housing: "Ruggedized for Ex environments",
    },
  },
  {
    id: "rxis101",
    name: "RxIS101",
    subtitle: "Android IS-Mobile",
    description: `The Realix RxIS101 is an advanced device specifically designed for professionals working in hazardous environments. It offers a perfect blend of exceptional safety features and cutting-edge technology. With its 4G connectivity and dual SIM capability, staying connected with your team has never been easier. The RxIS101 operates on the latest Android 12 operating system, providing a seamless and secure user experience. Its compact 2.83-inch TFT screen ensures clear visibility in any lighting conditions.

Thanks to its long-lasting 4200 mAh battery, the RxIS101 keeps you productive throughout your workday. Real-time communication is made effortless with its Push-to-Talk over cellular functionality. Built to endure tough conditions, the RxIS101 holds an IP68 rating, making it dust-proof, waterproof, and resistant to shocks and vibrations.
  
Additionally, it features NFC technology for secure data transfer and contactless transactions. With its powerful 120 dB sound output, you can communicate clearly even in noisy environments. In case of emergencies, the easily accessible SOS button can be used to signal for assistance. The RxIS101 also comes equipped with a 16MP rear camera, allowing you to capture important visuals with precision. Choose the Realix RxIS101 and unlock the ultimate combination of safety, performance, and innovation in an intrinsically safe mobile device.`,
    images: [
      "/images/products/mobile/rxis101-front.png",
      "/images/products/mobile/rxis101-back.png",
    ],
    features: [
      "Advanced Safety Features & Cutting-Edge Technology",
      "4G Connectivity & Dual SIM",
      "Android 12 Operating System",
      "Push-to-Talk Over Cellular (PoC)",
      "IP68 Dust-proof, Waterproof, Shock & Vibration Resistant",
      "NFC Technology & 120dB Sound Output",
      "SOS Button & 16MP Rear Camera",
    ],
    highlights: [
      { title: "OS", value: "Android 12", icon: "ion:logo-android" },
      { title: "Type", value: "Keypad", icon: "ion:keypad" },
    ],
    pdfUrl: "/documents/mobile/technical-specification/rxis101.pdf",
    technicalData: {
      "Operating System": "Android 12",
      Processor: "MTK6762 (2.0GHz Octa Core)",
      Memory: "4GB RAM + 64GB ROM",
      "External Memory": "Micro-SD up to 2TB",
    },
    mainCharacteristics: {
      Display: '2.83" TFT Rug Display (480 x 640)',
      Battery: "4200mAh Lithium Ion",
      Cameras: "Rear: 16MP | Front: 8MP",
      Buttons: "Full Keypad, SOS, PTT, Customizable Key",
      "IP Rating": "IP68",
      "ATEX Approval": "II 2G Ex ib IIC T4 Gb | II 2D Ex ib IIIC T135°C Db",
    },
    connectivity: {
      Network: "2G/3G/4G LTE Dual SIM",
      WiFi: "2.4GHz & 5GHz",
      Bluetooth: "4.2",
      GPS: "GPS+BDS / GPS+GLONASS",
      NFC: "Yes",
      PoC: "Push-to-Talk Over Cellular",
    },
    physicalCharacteristics: {
      Dimensions: "137.3 x 59.3 x 23.3 mm",
      Weight: "246g",
      "Operating Temp": "-20°C to +60°C",
    },
  },
  {
    id: "rxis02",
    name: "Rx IS-02",
    subtitle: "Android IS-Mobile",
    description: `The RxIS 02 is a rugged safety mobile phone designed for first responders and industrial workers. It provides reliable communication in hazardous environments.

It is an IS-certified Android phone built for Zone 1/21 explosive areas with a strong waterproof body and durable keypad design.

The phone includes features like Wi-Fi, SOS emergency key, amplified loudspeaker, and clear voice communication for safe field operations.

With 4GB RAM and 64GB storage, RxIS 02 delivers powerful performance while maintaining safety and reliability in challenging industrial conditions.`,
    images: [
      "/images/products/mobile/IS 02- Front.png"
    ],
    features: [
      "OPERATING SYSTEM: Android 14 Powered Device",
      "PROCESSOR: Mediatek Helio P35 Octa-Core 2.2GHz",
      "DISPLAY: 2.83\" IPS LCD with Glove & Wet Touch Support",
      "NETWORK: 4G LTE Dual SIM with Wi-Fi & Bluetooth 5.0",
      "MEMORY: 4GB RAM + 64GB Storage (Expandable up to 256GB)",
      "PROTECTION: IP68 Rugged Design for Harsh Environments",
    ],
    highlights: [
      { title: "OS", value: "Android 14", icon: "ion:logo-android" },
      { title: "Type", value: "Keypad", icon: "ion:keypad" },
    ],
    pdfUrl: "/documents/mobile/technical-specification/rxis02.pdf",
    technicalData: {
      "Operating System": "Android 14",
      Processor: "Mediatek Helio P35 Octa-Core 2.2GHz",
      Memory: "4GB RAM + 64GB ROM",
      "External Memory": "Micro SD up to 256GB",
    },
    mainCharacteristics: {
      Display: '2.83" IPS LCD (480x640), IK03, Glove/Wet Touch',
      Battery: "4000mAh, 3.7V Replaceable",
      Cameras: "Rear: 13MP | Front: 8MP",
      Network: "4G LTE (Global Bands)",
      "Protection Class": "IP68",
    },
    connectivity: {
      SIM: "Dual SIM (LTE Support)",
      WiFi: "Dual-band Wi-Fi (ac/a/b/g/n) & Hotspot",
      Bluetooth: "5.0",
      Navigation: "GPS/Beidou/GLONASS/AGPS/Galileo",
    },
    physicalCharacteristics: {
      Dimensions: "148 x 65 x 25 mm",
      Weight: "313g",
      "Ambient Temp": "-20°C to +55°C",
      Sensors: "Light, Distance, Gravity, E-Compass, Gyro, Hall",
      Standby: "260 Hrs Standby / 13 Hrs Talk Time",
    },
  },
];
